import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Box, Button, Popover, Typography, Tooltip } from '@mui/material';
import Iconify from './Iconify';
import usePracticeStaff from '../hooks/usePracticeStaff';
import useAuth from '../hooks/useAuth';
import { useSelector } from '../redux/store';
import { SUB_LABEL, AVATAR_COLORS } from '../styles/stepFormStyles';

/**
 * Inline avatar circle selector for practitioners or assignees.
 *
 * Props:
 *   role       — 'practitioner' | 'assignee' (default: 'assignee')
 *                 'practitioner' shows blue circles (PRACTITIONER role users)
 *                 'assignee' shows purple circles (TCO/USER role users)
 *   label      — optional uppercase section label above the avatars
 *   assignToState — array of currently selected user objects
 *   setAssignTo   — setter to update selection
 *   context    — 'task' | undefined
 *                 'task' uses the broad taskAssignableUsers list (all enabled users,
 *                 any role, including ADMIN/SUPPORT/PRACTITIONER/superuser) and
 *                 guarantees the currently logged-in user is always present.
 *                 Leave unset for the standard lead-facing assignee picker.
 *
 * Replaces both the old AssignedToLead popup AND UserSearchDropdown popup.
 */
const AssignedToLead = ({ label, assignToState = [], setAssignTo, staffRole = 'assignee', context, showSelectAll = false, dark = false, wrapAvatars = true, maxVisible = null }) => {
    const { assignableUsers, bookableStaff, isLoaded } = usePracticeStaff();
    // Anchor for the "+N" overflow popover. Only reachable when `maxVisible`
    // caps the row; unset otherwise, so this stays inert for every call site
    // that renders the full list.
    const [overflowAnchor, setOverflowAnchor] = useState(null);
    const { user: authUser } = useAuth();
    const practiceUserResults = useSelector((state) => state.practiceUser?.practiceUser?.results);

    const isTaskContext = context === 'task';
    const isPractitionerMode = staffRole === 'practitioner';

    // ADMIN users to include when the practice has fewer than 3 TCO/USER people.
    const adminUsers = useMemo(() => {
        if (!practiceUserResults) return [];
        return practiceUserResults
            .filter(el => el?.is_enabled && el?.role?.toUpperCase() === 'ADMIN' && !el?.user?.is_superuser)
            .map(el => ({
                ...el,
                _name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                _initials: ((el?.user?.first_name?.[0] || '') + (el?.user?.last_name?.[0] || '')).toUpperCase() || '?',
            }));
    }, [practiceUserResults]);

    // Task context uses TCO+USER only; expands to include ADMINs when fewer than 3
    // TCO/USER members are in the practice (small-practice fallback).
    const roleScopedBase = useMemo(
        () => (assignableUsers.length < 3 ? [...assignableUsers, ...adminUsers] : assignableUsers),
        [assignableUsers, adminUsers]
    );

    // For the task context: start from roleScopedBase (TCO+USER, with optional ADMIN
    // expansion) and guarantee the current logged-in user is always present, even if
    // they are disabled or otherwise excluded (e.g. edge-case data inconsistency).
    const taskUsers = useMemo(() => {
        if (!isTaskContext) return null;
        const base = roleScopedBase;
        const currentPk = authUser?.pk;
        if (!currentPk) return base;
        const alreadyPresent = base.some(u => u?.user?.id === currentPk);
        if (alreadyPresent) return base;
        // Current user missing from the enabled list — find their PracticeUser entry
        // in the full results array and prepend it so they can self-assign.
        const raw = practiceUserResults?.find(el => el?.user?.id === currentPk);
        if (!raw) return base;
        const mapped = {
            ...raw,
            _name: `${raw?.user?.first_name || ''} ${raw?.user?.last_name || ''}`.trim(),
            _initials: ((raw?.user?.first_name?.[0] || '') + (raw?.user?.last_name?.[0] || '')).toUpperCase() || '?',
        };
        return [mapped, ...base];
    }, [isTaskContext, roleScopedBase, authUser, practiceUserResults]);

    let users;
    if (isTaskContext) {
        users = taskUsers;
    } else if (isPractitionerMode) {
        users = bookableStaff;
    } else {
        users = assignableUsers;
    }
    const accentColor = isPractitionerMode ? AVATAR_COLORS.practitioner : AVATAR_COLORS.assignee;

    // Select-all / Clear-all helpers — only computed when showSelectAll is true.
    const allSelected = showSelectAll && users?.length > 0
        && users.every(u => assignToState?.some(a => String(a.id) === String(u.id)));
    const handleSelectAll = () => {
        if (allSelected) {
            setAssignTo([]);
        } else {
            setAssignTo([...(users || [])]);
        }
    };

    // String() coercion both sides: API returns `practice_user.id` as a
    // string ("1048") while usePracticeStaff returns it as a number (1048)
    // — `===` mismatches and the picker never shows an active border,
    // so a click "adds" an already-assigned user (duplicate). Coerce so
    // both sources of truth compare equal regardless of typeof.
    const handleToggle = (user) => {
        const targetId = String(user.id);
        const isSelected = assignToState?.some(a => String(a.id) === targetId);
        if (isSelected) {
            setAssignTo(assignToState.filter(a => String(a.id) !== targetId));
        } else {
            setAssignTo([...(assignToState || []), user]);
        }
    };

    if (!isLoaded) return label ? <Box><Typography sx={SUB_LABEL}>{label}</Typography><Typography sx={{ fontSize: '12px', color: '#919EAB' }}>Loading...</Typography></Box> : null;
    if (users.length === 0) return null;

    // On dark bars the light `${accentColor}14` bg is near-invisible. Use a
    // slightly higher-opacity tint so initials are readable against dark chrome.
    const circleRestBg = dark ? `${accentColor}30` : `${accentColor}14`;
    const circleRestColor = dark ? '#fff' : accentColor;
    // Selected-state border colour — keep the same accent ring on both surfaces.
    const circleBorderColor = dark ? '#fff' : AVATAR_COLORS.selected;

    // Overflow cap. On tight surfaces (the floating task bars) a 12-person
    // practice would otherwise put twelve 30px circles plus "Select all" up
    // against the action buttons for the same pixels. `maxVisible` unset means
    // render everyone, which is what every pre-existing call site does.
    const cap = maxVisible && maxVisible > 0 && users.length > maxVisible ? maxVisible : null;
    const visibleUsers = cap ? users.slice(0, cap) : users;
    const overflowUsers = cap ? users.slice(cap) : [];
    const overflowSelectedCount = overflowUsers.filter(
        (u) => assignToState?.some(a => String(a.id) === String(u.id))
    ).length;

    // flexShrink:0 here is load-bearing, not cosmetic. These circles are flex
    // items in a row that can be narrower than its content, and the default
    // flex-shrink:1 makes the browser resolve that overflow by squeezing
    // WIDTHS while heights stay put -- 30x30 circles render as ovals (so does
    // the inner <img>, itself a flex item) and the overflowX:'auto' below
    // never engages, because shrinking already consumed the overflow.
    const circleSx = (isSelected) => ({
        width: 30,
        height: 30,
        flexShrink: 0,
        borderRadius: '50%',
        position: 'relative',
        cursor: 'pointer',
        border: '2px solid',
        borderColor: isSelected ? circleBorderColor : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isSelected ? AVATAR_COLORS.selectedBg : circleRestBg,
        fontSize: '11px',
        fontWeight: 600,
        color: isSelected ? AVATAR_COLORS.selected : circleRestColor,
        transition: 'all 150ms ease',
        '&:hover': { borderColor: accentColor },
    });

    const avatarImgStyle = { width: 26, height: 26, flexShrink: 0, borderRadius: '50%', objectFit: 'cover' };
    const overflowRowSelectedBg = dark ? 'rgba(255,255,255,0.12)' : 'action.selected';
    const overflowRowHoverBg = dark ? 'rgba(255,255,255,0.08)' : 'action.hover';

    const renderCircle = (u) => {
        const isSelected = assignToState?.some(a => String(a.id) === String(u.id));
        return (
            <Tooltip key={u.id} title={u._name} arrow placement="top"
                componentsProps={{ tooltip: { sx: { bgcolor: accentColor, fontSize: '11px', fontWeight: 600 } }, arrow: { sx: { color: accentColor } } }}>
                <Box onClick={() => handleToggle(u)} data-testid={`assignee-circle-${u.id}`} sx={circleSx(isSelected)}>
                    {u.user?.avatar
                        ? <img src={u.user.avatar} alt="" style={avatarImgStyle} />
                        : u._initials}
                    {isSelected && <Box sx={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12, borderRadius: '50%', background: AVATAR_COLORS.selected, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #fff' }}><Iconify icon="eva:checkmark-fill" width={8} sx={{ color: '#fff' }} /></Box>}
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            {label && (
                <Typography sx={SUB_LABEL}>
                    {label}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: wrapAvatars ? 'wrap' : 'nowrap',
                    maxWidth: '100%',
                    overflowX: wrapAvatars ? 'visible' : 'auto',
                    overflowY: 'hidden',
                    pb: wrapAvatars ? 0 : 0.25,
                    pr: wrapAvatars ? 0 : 0.5,
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': {
                        borderRadius: 999,
                        bgcolor: dark ? 'rgba(255,255,255,0.24)' : 'rgba(99,115,129,0.32)',
                    },
                }}
                data-testid="assignee-avatar-row"
            >
                {visibleUsers.map((u) => renderCircle(u))}
                {overflowUsers.length > 0 && (
                    <Tooltip title={`${overflowUsers.length} more`} arrow placement="top"
                        componentsProps={{ tooltip: { sx: { bgcolor: accentColor, fontSize: '11px', fontWeight: 600 } }, arrow: { sx: { color: accentColor } } }}>
                        <Box
                            onClick={(e) => setOverflowAnchor(e.currentTarget)}
                            data-testid="assignee-overflow-toggle"
                            sx={{ ...circleSx(overflowSelectedCount > 0), fontSize: '10px', letterSpacing: '-0.2px' }}
                        >
                            +{overflowUsers.length}
                        </Box>
                    </Tooltip>
                )}
                {showSelectAll && users.length > 0 && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={handleSelectAll}
                        data-testid="assignee-select-all"
                        sx={{
                            ml: 0.5,
                            // Same trap as the circles: without these the label
                            // wraps to two lines the moment the row is squeezed.
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            fontSize: 12,
                            textTransform: 'none',
                            color: dark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                            minWidth: 0,
                            px: 0.75,
                            '&:hover': {
                                color: dark ? '#fff' : 'text.primary',
                                bgcolor: dark ? 'rgba(255,255,255,0.08)' : 'action.hover',
                            },
                        }}
                    >
                        {allSelected ? 'Clear all' : 'Select all'}
                    </Button>
                )}
            </Box>

            {/* Everyone past the cap -- still selectable, but from a popover
                instead of by growing the row past the space it has. */}
            <Popover
                open={Boolean(overflowAnchor)}
                anchorEl={overflowAnchor}
                onClose={() => setOverflowAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        mt: -1,
                        p: 1,
                        maxHeight: 320,
                        bgcolor: dark ? 'rgba(20, 22, 28, 0.94)' : 'background.paper',
                        backdropFilter: dark ? 'blur(20px) saturate(180%)' : 'none',
                        WebkitBackdropFilter: dark ? 'blur(20px) saturate(180%)' : 'none',
                        border: dark ? '1px solid rgba(255,255,255,0.1)' : undefined,
                        borderRadius: '12px',
                        color: dark ? '#fff' : 'inherit',
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 200 }}>
                    {overflowUsers.map((u) => {
                        const isSelected = assignToState?.some(a => String(a.id) === String(u.id));
                        return (
                            <Box
                                key={u.id}
                                onClick={() => handleToggle(u)}
                                data-testid={`assignee-overflow-${u.id}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 0.75,
                                    py: 0.5,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    bgcolor: isSelected ? overflowRowSelectedBg : 'transparent',
                                    '&:hover': { bgcolor: overflowRowHoverBg },
                                }}
                            >
                                <Box sx={{ ...circleSx(isSelected), cursor: 'inherit' }}>
                                    {u.user?.avatar
                                        ? <img src={u.user.avatar} alt="" style={avatarImgStyle} />
                                        : u._initials}
                                </Box>
                                <Typography sx={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, whiteSpace: 'nowrap' }}>
                                    {u._name || u._initials}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Popover>
        </Box>
    );
};

AssignedToLead.propTypes = {
    label: PropTypes.string,
    assignToState: PropTypes.array,
    setAssignTo: PropTypes.any,
    staffRole: PropTypes.oneOf(['practitioner', 'assignee']),
    /** Pass context="task" to use the broad task-assignable list (all roles + superusers). */
    context: PropTypes.oneOf(['task']),
    /** Show a "Select all / Clear all" toggle next to the avatar row. Off by default. */
    showSelectAll: PropTypes.bool,
    /** Dark-surface mode — increases circle contrast for legibility on dark bars. */
    dark: PropTypes.bool,
    /** Keep avatars on one scrollable row instead of wrapping. */
    wrapAvatars: PropTypes.bool,
    /** Cap the inline row at N avatars and put the rest behind a "+N" popover. Unset = show all. */
    maxVisible: PropTypes.number,
};

AssignedToLead.defaultProps = {
    label: undefined,
    assignToState: [],
    setAssignTo: undefined,
    staffRole: 'assignee',
    context: undefined,
    showSelectAll: false,
    dark: false,
    wrapAvatars: true,
    maxVisible: null,
};

export default AssignedToLead;
